import { api } from './api';

export interface LlmUsageRow {
  id: string;
  user_id: string | null;
  /* true = traitement système, légitimement sans utilisateur. */
  is_system: boolean;
  user_email: string | null;
  school_id: string | null;
  school_name: string | null;
  thread_id: string | null;
  content_document_id: string | null;
  content_document_title: string | null;
  exam_copy_id: string | null;
  exam_session_id: string | null;
  exam_title: string | null;
  assignment_id: string | null;
  assignment_title: string | null;
  kind: 'text' | 'voice' | 'embedding' | null;
  rate_match: 'exact' | 'alias' | 'unpriced' | null;
  provider: string | null;
  model: string | null;
  feature: string | null;
  duration_ms: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens: number;
  cache_creation_input_tokens: number;
  thinking_tokens: number;
  image_input_tokens: number;
  video_input_tokens: number;
  document_input_tokens: number;
  audio_input_tokens: number;
  audio_output_tokens: number;
  estimated_cost_usd: number | null;
  created_at: string;
  llm_metadata: Record<string, unknown> | null;
}

export interface LlmUsageFacets {
  providers: string[];
  models: string[];
  features: string[];
}

/* Derived from the tRPC input rather than redeclared: provider/feature are
 * enums server-side, and a hand-copied union here would drift the day one
 * gains a value. */
export type LlmUsageFilter = NonNullable<Exclude<Parameters<typeof api.admin.llmUsage.query>[0], void>>;

export async function fetchLlmUsage(filter?: LlmUsageFilter): Promise<{ items: LlmUsageRow[]; total: number; totalCostUsd: number; unpricedCount: number }> {
  return (await api.admin.llmUsage.query(filter)) as { items: LlmUsageRow[]; total: number; totalCostUsd: number; unpricedCount: number };
}

export async function fetchLlmUsageFacets(range?: { sinceIso?: string; untilIso?: string }): Promise<LlmUsageFacets> {
  return (await api.admin.llmUsageFacets.query(range)) as LlmUsageFacets;
}

export interface UsageRow {
  id: string;
  /* Nullable : une ligne système n'a pas d'utilisateur (cf. is_system). */
  user_id: string | null;
  thread_id: string | null;
  kind: 'text' | 'voice' | 'embedding' | null;
  duration_ms: number;
  input_tokens: number;
  output_tokens: number;
  audio_input_tokens: number;
  audio_output_tokens: number;
  estimated_cost_usd: number | null;
  created_at: string;
}

export interface ThreadRow {
  id: string;
  user_id: string;
  mode: 'general' | 'parcours' | 'voice' | 'cours';
  title: string;
  last_message_at: string;
  created_at: string;
}

export interface AdminStats {
  totalUsd: number;
  textUsd: number;
  voiceUsd: number;
  totalRecords: number;
  uniqueUsers: number;
  voiceSeconds: number;
  inputTokens: number;
  outputTokens: number;
  audioInputTokens: number;
  audioOutputTokens: number;
  /* Threads that have at least one usage_records row attached. */
  threadsWithUsage: number;
  /* Total cost / threadsWithUsage. 0 if no thread was billed. */
  avgCostPerThread: number;
  /* Appels sans grille tarifaire : leur coût est inconnu, pas nul. Sans ce
     compteur, totalUsd se lirait comme une dépense complète. */
  unpricedRecords: number;
  /* Effective $/min on the voice path — voiceUsd / voiceMinutes. Sanity */
  /* check against the published rate ($0.005 in + $0.018 out = $0.023/min). */
  /* Diverges from the rate only if we ever store something other than the */
  /* duration-derived cost in usage_records. */
  avgUsdPerVoiceMinute: number;
  /* Average text tokens per conversation (in / out). Pure text rows */
  /* dominate this since voice rows store 0/0 in the text columns. */
  avgTextTokensInPerThread: number;
  avgTextTokensOutPerThread: number;
  /* Average audio tokens per conversation. Reported by Gemini Live in */
  /* usageMetadata.{prompt,response}TokensDetails with modality=AUDIO. */
  /* Voice rows carry these; text rows are 0. */
  avgAudioTokensInPerThread: number;
  avgAudioTokensOutPerThread: number;
}

export interface ThreadUsage {
  textIn: number;
  textOut: number;
  audioIn: number;
  audioOut: number;
  audioSec: number;
  costUsd: number;
}

/** Returns true if the current user holds a platform 'admin' role assignment.
 *  Table-driven (role_assignments) — the single source of truth for roles.
 *  Uses getSession() (local) to avoid a JWT-verify round-trip that can fail
 *  silently on flaky mobile connections. */
/* CLEAN-ARCH (Vague 4) : via les ports tRPC (api.admin.*, gating server-side) */
/* au lieu de supabase-js direct. */
export async function checkIsAdmin(): Promise<boolean> {
  try {
    return await api.admin.access.query();
  } catch {
    return false;
  }
}

/** Same shape as {@link checkIsAdmin}, but also admits the read-only
 *  `ai_operator` role — gates the subset of /admin/* pages that role covers
 *  (conversations, llm usage, safeguarding, curriculum, prompts). Use
 *  `checkIsAdmin()` within those pages to decide whether to show mutation UI. */
export async function checkIsAdminOrAiOperator(): Promise<boolean> {
  try {
    return await api.admin.accessReadOnly.query();
  } catch {
    return false;
  }
}

/** All usage rows since `since` (defaults to 30 days ago, server-side). */
export async function fetchUsage(since?: Date): Promise<UsageRow[]> {
  return (await api.admin.usage.query({ sinceIso: since?.toISOString() })) as UsageRow[];
}

export async function fetchThreads(limit = 100): Promise<ThreadRow[]> {
  return (await api.admin.threads.query({ limit })) as ThreadRow[];
}

/** Hard-delete: cascade removes the thread + its messages (admin-only). */
export async function deleteThread(threadId: string): Promise<void> {
  await api.admin.deleteThread.mutate({ threadId });
}

export interface ReleaseRow {
  id: string;
  platform: 'android' | 'ios';
  profile: 'production' | 'preview' | 'development';
  version: string;
  url: string;
  build_id: string | null;
  notes: string | null;
  created_at: string;
}

/** Lists every release across platforms + profiles. Public-read RLS lets
 *  this query run from the admin page without any extra auth check. */
export async function fetchAllReleases(limit = 50): Promise<ReleaseRow[]> {
  return (await api.admin.releases.query({ limit })) as ReleaseRow[];
}

export function summarize(rows: UsageRow[]): AdminStats {
  let textUsd = 0,
    voiceUsd = 0,
    voiceMs = 0,
    textIn = 0,
    textOut = 0,
    audioIn = 0,
    audioOut = 0;
  const users = new Set<string>();
  const threads = new Set<string>();
  let unpriced = 0;
  for (const r of rows) {
    /* Sans ce garde, chaque ligne système ajoutait `null` au Set et gonflait
       le compteur d'utilisateurs uniques de un. */
    if (r.user_id) users.add(r.user_id);
    if (r.thread_id) threads.add(r.thread_id);
    if (r.estimated_cost_usd === null) unpriced += 1;
    const cost = r.estimated_cost_usd === null ? 0 : Number(r.estimated_cost_usd);
    if (r.kind === 'voice') {
      voiceUsd += cost;
      voiceMs += r.duration_ms;
    } else {
      textUsd += cost;
    }
    textIn += r.input_tokens;
    textOut += r.output_tokens;
    audioIn += r.audio_input_tokens;
    audioOut += r.audio_output_tokens;
  }
  const totalUsd = textUsd + voiceUsd;
  const voiceMinutes = voiceMs / 60_000;
  return {
    totalUsd,
    textUsd,
    voiceUsd,
    totalRecords: rows.length,
    uniqueUsers: users.size,
    voiceSeconds: voiceMs / 1000,
    inputTokens: textIn,
    outputTokens: textOut,
    audioInputTokens: audioIn,
    audioOutputTokens: audioOut,
    threadsWithUsage: threads.size,
    avgCostPerThread: threads.size > 0 ? totalUsd / threads.size : 0,
    unpricedRecords: unpriced,
    avgUsdPerVoiceMinute: voiceMinutes > 0 ? voiceUsd / voiceMinutes : 0,
    avgTextTokensInPerThread: threads.size > 0 ? textIn / threads.size : 0,
    avgTextTokensOutPerThread: threads.size > 0 ? textOut / threads.size : 0,
    avgAudioTokensInPerThread: threads.size > 0 ? audioIn / threads.size : 0,
    avgAudioTokensOutPerThread: threads.size > 0 ? audioOut / threads.size : 0,
  };
}

/** Aggregates usage rows by thread_id so the admin table can show tokens
 *  + audio seconds + cost per conversation row. Rows without thread_id are
 *  dropped (legacy voice path used conversation_id; not surfaced here). */
export function usageByThread(rows: UsageRow[]): Map<string, ThreadUsage> {
  const m = new Map<string, ThreadUsage>();
  for (const r of rows) {
    if (!r.thread_id) continue;
    const u = m.get(r.thread_id) ?? { textIn: 0, textOut: 0, audioIn: 0, audioOut: 0, audioSec: 0, costUsd: 0 };
    u.textIn += r.input_tokens;
    u.textOut += r.output_tokens;
    u.audioIn += r.audio_input_tokens;
    u.audioOut += r.audio_output_tokens;
    if (r.kind === 'voice') u.audioSec += r.duration_ms / 1000;
    /* Un appel non tarifé n'ajoute rien plutôt qu'un zéro affirmé. */
    u.costUsd += r.estimated_cost_usd === null ? 0 : Number(r.estimated_cost_usd);
    m.set(r.thread_id, u);
  }
  return m;
}

export async function fetchUserEmails(userIds: string[]): Promise<Map<string, string>> {
  if (userIds.length === 0) return new Map();
  const rows = await api.admin.userEmails.query({ userIds });
  return new Map(rows.map((r) => [r.id, r.email]));
}

export function usd(n: number): string {
  return `$${n.toFixed(n < 0.01 ? 4 : 2)}`;
}

/** Higher-precision USD for per-unit rates (per minute, per token, …)
 *  where rounding to 2 decimals hides the relevant digits. */
export function usdRate(n: number): string {
  if (n === 0) return '$0';
  if (n < 0.001) return `$${n.toFixed(5)}`;
  return `$${n.toFixed(4)}`;
}
